import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import Typography from '@mui/material/Typography';

import LIcon from '@/assets/deliverySize/LIcon';
import MIcon from '@/assets/deliverySize/MIcon';
import SIcon from '@/assets/deliverySize/SIcon';
import XlIcon from '@/assets/deliverySize/XlIcon';
import XsIcon from '@/assets/deliverySize/XsIcon';
import Icon99M from '@/assets/services/Icon99M';
import Icon99MinutosGrey from '@/assets/services/Icon99MinutosGrey';
import IconCoo2Fre from '@/assets/services/IconCoo2Fre';
import IconCoo2FreeGreen from '@/assets/services/IconCoo2FreeGreen';
import IconNational from '@/assets/services/IconNational';
import IconNationalGreen from '@/assets/services/IconNationalGreen';
import IconRetorno from '@/assets/services/IconRetorno';
import IconRetornoGreen from '@/assets/services/IconRetornoGreen';
import IconSameDay from '@/assets/services/IconSameDay';
import IconSameDayGreen from '@/assets/services/IconSameDayGreen';
import IconSprint from '@/assets/services/IconSprint';
import IconSprintGreen from '@/assets/services/IconSprintGreen';
import { PackageSizeData } from '@/types/parcel/userData/profile.interface';

export const deliveryTypeChips = [
  { label: 'Same Day', value: 'SMD' },
  { label: 'Sprint', value: 'SPT' },
  { label: '99minutos', value: '99M' },
  { label: 'CO2 Free', value: 'CO2F' },
  { label: 'Retorno', value: 'RET' },
  { label: 'Nacional', value: 'NAL' },
];

export const originAddressForm = [
  { name: 'origin.address', label: 'Calle *' },
  { name: 'origin.externalNumber', label: 'Número exterior *' },
  { name: 'origin.internalNumber', label: 'Número interior' },
  { name: 'origin.zipCode', label: 'Código postal *' },
  { name: 'origin.cologne', label: 'Colonia *' },
  { name: 'origin.state', label: 'Estado *' },
  { name: 'origin.city', label: 'Ciudad *' },
];

const defaultSizeChips = [
  { label: 'Menos de 1 kg', value: 'xs' },
  { label: 'De 1 a 2 kg', value: 's' },
  { label: 'De 2 a 3 kg', value: 'm' },
  { label: 'De 3 a 5 kg', value: 'l' },
  { label: 'De 5 a 20kg', value: 'xl' },
];

const defaultSizeRanges = [
  { code: 'xs', text: 'Menos de 1 kg', min: 0, max: 1 },
  { code: 's', text: 'De 1 a 2 kg', min: 1, max: 2 },
  { code: 'm', text: 'De 2 a 3 kg', min: 2, max: 3 },
  { code: 'l', text: 'De 3 a 5 kg', min: 3, max: 5 },
  { code: 'xl', text: 'De 5 a 20kg', min: 5, max: 20 },
];

const SIZE_ORDER = ['xs', 's', 'm', 'l', 'xl'];

const formatKg = (grams: number) => {
  const kg = grams / 1000;
  return Number.isInteger(kg) ? `${kg}` : `${Math.round(kg * 10) / 10}`;
};

const getOrderedSizeWeights = (packageSizes?: PackageSizeData[]) => {
  if (!packageSizes || packageSizes.length === 0) return null;

  const bySize = new Map(
    packageSizes.map(({ size, maxWeight }) => [size.toLowerCase(), maxWeight]),
  );

  const orderedSizes = SIZE_ORDER.filter((size) => bySize.has(size));
  if (orderedSizes.length === 0) return null;

  return orderedSizes.map((size, index) => ({
    value: size,
    minWeight: index === 0 ? 0 : (bySize.get(orderedSizes[index - 1]) as number),
    maxWeight: bySize.get(size) as number,
  }));
};

export const getSizeChips = (packageSizes?: PackageSizeData[]) => {
  const weights = getOrderedSizeWeights(packageSizes);
  if (!weights) return defaultSizeChips;

  return weights.map(({ value, minWeight, maxWeight }, index) => ({
    value,
    label:
      index === 0
        ? `Menos de ${formatKg(maxWeight)} kg`
        : `De ${formatKg(minWeight)} a ${formatKg(maxWeight)} kg`,
  }));
};

export const getSizeRanges = (packageSizes?: PackageSizeData[]) => {
  const weights = getOrderedSizeWeights(packageSizes);
  if (!weights) return defaultSizeRanges;

  return weights.map(({ value, minWeight, maxWeight }, index) => ({
    code: value,
    text:
      index === 0
        ? `Menos de ${formatKg(maxWeight)} kg`
        : `De ${formatKg(minWeight)} a ${formatKg(maxWeight)} kg`,
    min: minWeight / 1000,
    max: maxWeight / 1000,
  }));
};

export const senderData = [
  {
    label: 'Nombre',
    name: 'sender.firstName',
    Icon: <PersonOutlineOutlinedIcon />,
    placeholder: 'Quien envía',
  },
  {
    label: 'Apellido',
    name: 'sender.lastName',
    Icon: <PersonOutlineOutlinedIcon />,
    placeholder: '',
  },
  { label: 'Teléfono', name: 'sender.phone', Icon: <PhoneIphoneOutlinedIcon /> },
  { label: 'Email', name: 'sender.email', Icon: <EmailOutlinedIcon /> },
] as const;

export const destinationAddressForm = [
  { name: 'destination.address', label: 'Calle *' },
  { name: 'destination.externalNumber', label: 'Número exterior *' },
  { name: 'destination.internalNumber', label: 'Número interior' },
  { name: 'destination.zipCode', label: 'Código postal *' },
  { name: 'destination.cologne', label: 'Colonia *' },
  { name: 'destination.state', label: 'Estado *' },
  { name: 'destination.city', label: 'Ciudad *' },
];

export const recipientData = [
  {
    label: 'Nombre',
    name: 'recipient.firstName',
    Icon: <PersonOutlineOutlinedIcon />,
    placeholder: 'Quien recibe',
  },
  {
    label: 'Apellido',
    name: 'recipient.lastName',
    Icon: <PersonOutlineOutlinedIcon />,
    placeholder: '',
  },
  { label: 'Teléfono', name: 'recipient.phone', Icon: <PhoneIphoneOutlinedIcon /> },
  { label: 'Email', name: 'recipient.email', Icon: <EmailOutlinedIcon /> },
] as const;

export const pickupOptions = [
  { value: false, label: 'Lo decidiré mas tarde' },
  { value: true, label: 'Agendar en este momento' },
];

export const getAdditionalOptions = (_insurancePercentage?: number) => [
  {
    checkName: 'payments.insured',
    checkLabel: 'Asegurar envío',
    inputName: 'payments.insuredValue',
    description: (
      <Typography variant="body2">
        El costo del seguro se <strong>calculará enseguida</strong> en función del valor declarado.
        En caso de daño o pérdida te reembolsaremos el monto ingresado en 3 días hábiles. Al hacer
        uso del servicio, aceptas los <span>términos y condiciones</span>.
      </Typography>
    ),
  },
  // {
  //   checkName: 'payments.cashOnDelivery.cod',
  //   checkLabel: 'Pago contra entrega',
  //   inputName: 'payments.cashOnDelivery.amount',
  //   description: (
  //     <Typography variant="body2">
  //       El pago contra entrega es una forma segura de pagar el contenido de tu paquete, con la
  //       confianza de que quien lo recibe; lo tiene en sus manos y tu dinero será depositado por el
  //       equipo de 99Minutos. <strong>El costo del servicio es del 4% del valor declarado</strong>,
  //       al hacer uso de este servicio aceptas <span>términos y condiciones</span>.
  //     </Typography>
  //   ),
  // },
];

export const iconProducts = (value: string, isSelected: boolean): React.ReactElement => {
  const iconStyle = { width: '24px', height: '24px' };

  if (isSelected) {
    switch (value) {
      case 'SMD':
        return <IconSameDayGreen sx={iconStyle} />;
      case 'SPT':
        return <IconSprintGreen sx={iconStyle} />;
      case 'NAL':
        return <IconNationalGreen sx={iconStyle} />;
      case 'CO2F':
        return <IconCoo2FreeGreen sx={iconStyle} />;
      case 'RET':
        return <IconRetornoGreen sx={iconStyle} />;
      default:
        return <Icon99M sx={iconStyle} />;
    }
  }
  switch (value) {
    case 'SMD':
      return <IconSameDay sx={iconStyle} />;
    case 'SPT':
      return <IconSprint sx={iconStyle} />;
    case 'NAL':
      return <IconNational sx={iconStyle} />;
    case 'CO2F':
      return <IconCoo2Fre sx={iconStyle} />;
    case 'RET':
      return <IconRetorno sx={iconStyle} />;
    default:
      return <Icon99MinutosGrey sx={iconStyle} />;
  }
};

export const iconSize = (value: string, isSelected: boolean) => {
  if (isSelected) {
    // Intercambiar estos iconos por los iconos en verde (excepto el de 99minutos)
    switch (value) {
      case 'xs':
        return <XsIcon sx={{ width: '21px' }} />;
      case 's':
        return <SIcon sx={{ width: '21px' }} />;
      case 'm':
        return <MIcon sx={{ width: '21px' }} />;
      case 'l':
        return <LIcon sx={{ width: '21px' }} />;
      case 'xl':
        return <XlIcon sx={{ width: '21px' }} />;
      default:
        return <IconRetorno sx={{ width: '2.2rem', height: '2.2rem' }} />;
    }
  }
  switch (value) {
    case 'xs':
      return <XsIcon sx={{ width: '21px' }} />;
    case 's':
      return <SIcon sx={{ width: '21px' }} />;
    case 'm':
      return <MIcon sx={{ width: '21px' }} />;
    case 'l':
      return <LIcon sx={{ width: '21px' }} />;
    case 'xl':
      return <XlIcon sx={{ width: '21px' }} />;
    default:
      // cambiar este icono por el de 99minutos en gris
      return <Icon99M sx={{ width: '2.2rem', height: '2.2rem' }} />;
  }
};
